import React, { useEffect, useState, useCallback } from 'react';
import { Award, AlertTriangle, Download, Loader2, CheckCircle2, ShieldAlert, Radio, UserCheck, Bot, BookOpen } from 'lucide-react';
import { assignDeviceViaN8n } from '../../services/n8n';
import { useRoomDeviceBinding } from '../../hooks/useRoomDeviceBinding';
import RoomBindingCard from '../../components/tabletop/RoomBindingCard';
import SeatDeviceSelect from '../../components/tabletop/SeatDeviceSelect';
import {
  tabletopGradingAPI,
  tabletopRunAPI,
  tabletopPersonasAPI,
  TabletopExercise,
  TabletopGrading,
  TabletopFlag,
  TabletopSeatState,
  TabletopPersona,
  TabletopFrameworkPackId,
  TabletopScorecardEntry,
  TabletopBehaviouralRating,
} from '../../services/tabletopService';

const CONTROL_ICON: Record<string, React.ElementType> = {
  human: UserCheck,
  ai_driven: Bot,
  referenced_only: BookOpen,
};

interface Props {
  projectId: string;
  exercise: TabletopExercise;
}

const RATING_COLOR: Record<TabletopBehaviouralRating, string> = {
  not_evident: 'bg-red-100 text-red-700 border-red-200',
  developing: 'bg-amber-100 text-amber-700 border-amber-200',
  effective: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  exemplary: 'bg-primary/10 text-primary border-primary/30',
};

const RATING_LABEL: Record<TabletopBehaviouralRating, string> = {
  not_evident: 'Not evident',
  developing: 'Developing',
  effective: 'Effective',
  exemplary: 'Exemplary',
};

const DIMENSION_LABEL: Record<string, string> = {
  plan_adequacy: 'Plan adequacy',
  plan_accessibility_under_duress: 'Plan accessibility under duress',
  decision_ownership_vs_supplier_deference: 'Decision ownership vs supplier deference',
  detection_and_escalation_speed: 'Detection & escalation speed',
  decision_making_quality: 'Decision-making quality',
  governance_and_authorisation: 'Governance & authorisation',
  regulatory_and_legal_compliance: 'Regulatory & legal compliance',
  cross_boundary_coordination: 'Cross-boundary coordination',
  communications: 'Communications',
  political_and_member_management: 'Political & member management',
  business_continuity_and_service_prioritisation: 'Business continuity & service prioritisation',
  adaptability: 'Adaptability',
  individual_persona_performance: 'Individual persona/role performance',
};

function ScorecardTable({ entries }: { entries: TabletopScorecardEntry[] }) {
  if (entries.length === 0) {
    return <p className="text-sm text-slate-400 text-center py-6">No scorecard entries.</p>;
  }
  return (
    <div className="divide-y divide-slate-100">
      {entries.map(entry => (
        <div key={entry.dimension} className="px-5 py-3 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy">{DIMENSION_LABEL[entry.dimension] || entry.dimension}</p>
            <p className="text-xs text-slate-400 mt-0.5">{entry.csfRefs.join(', ')}</p>
            {entry.evidence.map((e, i) => (
              <p key={i} className="text-xs text-slate-500 mt-1">"{e.note}"</p>
            ))}
          </div>
          <span className={`flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border ${RATING_COLOR[entry.rating]}`}>
            {RATING_LABEL[entry.rating]}
          </span>
        </div>
      ))}
    </div>
  );
}

const ResultsPage: React.FC<Props> = ({ projectId, exercise }) => {
  const runId = exercise.currentRunId;
  const [grading, setGrading] = useState<TabletopGrading | null | undefined>(undefined);
  const [flags, setFlags] = useState<TabletopFlag[]>([]);
  const [seats, setSeats] = useState<TabletopSeatState[]>([]);
  const [personas, setPersonas] = useState<TabletopPersona[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null);
  const [exportingPack, setExportingPack] = useState<TabletopFrameworkPackId | null>(null);
  const [pairingPersonaId, setPairingPersonaId] = useState<string | null>(null);

  // Post-session device reassignment — same room-then-device allocation as
  // Cast Setup (before) and the Run Console (during). Useful to fix up a
  // seat's pairing before re-running the same cast, or just to review it.
  const binding = useRoomDeviceBinding(exercise.sessionId);

  const load = useCallback(async () => {
    if (!runId) return;
    const [gradingRes, runRes] = await Promise.all([
      tabletopGradingAPI.get(projectId, exercise.exerciseId, runId),
      tabletopRunAPI.get(projectId, runId),
    ]);
    setGrading(gradingRes.data.grading);
    setFlags(runRes.data.flags);
    setSeats(runRes.data.seats);
  }, [projectId, exercise.exerciseId, runId]);

  useEffect(() => {
    load();
    if (grading) return; // stop polling once it's landed
    // Grading lands shortly after ENDEX, not instantly — poll briefly until it appears.
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [load, grading]);

  useEffect(() => {
    tabletopPersonasAPI.list(projectId, exercise.exerciseId).then(r => setPersonas(r.data.personas));
  }, [projectId, exercise.exerciseId]);

  const reassignDevice = async (seat: TabletopSeatState, deviceId: string) => {
    if (!exercise.sessionId || !runId) return;
    setPairingPersonaId(seat.personaId);
    try {
      if (deviceId) await assignDeviceViaN8n(deviceId, seat.personaId, exercise.sessionId);
      await tabletopRunAPI.setSeatDevice(projectId, runId, seat.personaId, deviceId || undefined);
      await Promise.all([binding.reload(), load()]);
    } finally {
      setPairingPersonaId(null);
    }
  };

  const personaById = new Map(personas.map(p => [p.personaId, p]));

  const exportEvidencePack = async (packId: TabletopFrameworkPackId) => {
    setExportingPack(packId);
    try {
      const r = await tabletopGradingAPI.evidencePack(projectId, exercise.exerciseId, runId!, packId);
      const blob = new Blob([JSON.stringify(r.data.evidencePack, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exercise.exerciseId}-${packId}-evidence-pack.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportingPack(null);
    }
  };

  if (!runId) {
    return <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-8">This exercise never ran.</div>;
  }

  if (grading === undefined) {
    return <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-8">Loading results…</div>;
  }

  const personaIds = grading ? Object.keys(grading.personaLevel) : [];

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1000px] mx-auto flex flex-col gap-6">

        <header className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <Award size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Results</h1>
            <p className="text-slate-500 text-sm font-medium">
              {grading ? `Graded ${new Date(grading.gradedAt).toLocaleString()}` : 'Grading in progress — this appears automatically once it completes.'}
            </p>
          </div>
        </header>

        {!grading && (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl p-8 flex items-center justify-center gap-3 text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin" /> Waiting for the grading agent…
          </div>
        )}

        {flags.length > 0 && (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" />
              <h2 className="text-sm font-bold text-navy">Flags ({flags.length})</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {flags.map(flag => (
                <div key={flag.flagId} className="px-5 py-3 flex items-start gap-3">
                  <ShieldAlert size={14} className={`mt-0.5 flex-shrink-0 ${flag.severity === 'high' ? 'text-red-500' : flag.severity === 'medium' ? 'text-amber-500' : 'text-slate-400'}`} />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{flag.type.replace(/_/g, ' ')}</p>
                    <p className="text-sm text-navy mt-0.5">{flag.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {seats.length > 0 && (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center gap-2">
              <Radio size={16} className="text-primary" />
              <h2 className="text-sm font-bold text-navy">Cast & devices</h2>
              <p className="text-xs text-slate-400 ml-2">Reassign a seat's device — useful before re-running this cast.</p>
            </div>
            <div className="divide-y divide-slate-100">
              {seats.map(seat => {
                const persona = personaById.get(seat.personaId);
                const ControlIcon = CONTROL_ICON[seat.control] || Bot;
                return (
                  <div key={seat.personaId} className="px-5 py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0 flex items-center gap-2">
                      <ControlIcon size={13} className="text-slate-400 flex-shrink-0" />
                      <p className="text-sm text-navy font-medium truncate">{persona?.displayName || seat.personaId}</p>
                    </div>
                    {seat.deliveryChannel === 'device_voice' ? (
                      <div className="w-56 flex-shrink-0">
                        <SeatDeviceSelect
                          personaId={seat.personaId}
                          value={seat.deviceId}
                          roomId={binding.roomId}
                          roomDevices={binding.roomDevices}
                          allDevices={binding.allDevices}
                          disabled={pairingPersonaId === seat.personaId}
                          onChange={deviceId => reassignDevice(seat, deviceId)}
                        />
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 flex-shrink-0">{seat.deliveryChannel.replace(/_/g, ' ')}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {seats.length > 0 && <RoomBindingCard binding={binding} />}

        {grading && (
          <>
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
                <h2 className="text-sm font-bold text-navy">Organisation-level scorecard</h2>
                <div className="flex items-center gap-2">
                  {(['csf-2.0', 'uk-caf'] as TabletopFrameworkPackId[])
                    .filter(p => exercise.frameworkPacks.includes(p))
                    .map(packId => (
                      <button
                        key={packId}
                        onClick={() => exportEvidencePack(packId)}
                        disabled={exportingPack === packId}
                        className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark disabled:opacity-50"
                      >
                        {exportingPack === packId ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                        Export {packId === 'csf-2.0' ? 'CSF 2.0' : 'UK CAF'} evidence pack
                      </button>
                    ))}
                </div>
              </div>
              <ScorecardTable entries={grading.orgLevel} />
            </div>

            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-[#e2e8f0]">
                <h2 className="text-sm font-bold text-navy">Coverage statement</h2>
              </div>
              <div className="px-5 py-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 size={14} className="text-emerald-500" />
                  <span className="text-xs font-semibold text-slate-500">Evidenced: {grading.coverageSummary.functionsEvidenced.join(', ')}</span>
                </div>
                <p className="text-sm text-slate-600">{grading.coverageSummary.note}</p>
              </div>
            </div>

            {personaIds.length > 0 && (
              <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center gap-2 flex-wrap">
                  <h2 className="text-sm font-bold text-navy mr-2">Per-seat coaching</h2>
                  {personaIds.map(id => (
                    <button
                      key={id}
                      onClick={() => setSelectedPersona(id === selectedPersona ? null : id)}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${selectedPersona === id ? 'bg-primary text-white border-primary' : 'bg-white text-slate-500 border-slate-200'}`}
                    >
                      {id}
                    </button>
                  ))}
                </div>
                {selectedPersona && <ScorecardTable entries={grading.personaLevel[selectedPersona] || []} />}
                {!selectedPersona && <p className="px-5 py-6 text-sm text-slate-400 text-center">Select a seat to see its scorecard.</p>}
              </div>
            )}

            <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
              <h2 className="text-sm font-bold text-navy mb-3">Recommendations</h2>
              <ul className="flex flex-col gap-1.5">
                {grading.recommendations.map((rec, i) => (
                  <li key={i} className="text-sm text-slate-600">• {rec}</li>
                ))}
                {grading.recommendations.length === 0 && <p className="text-sm text-slate-400">None recorded.</p>}
              </ul>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default ResultsPage;
